import React, {useCallback, useMemo, useRef} from 'react';
import '../../styles/components/dashboard/bootcamp-progress-card.scss';
import useIntersectionObserver from "../../services/intersection/intersection";
import {FontIcon, Icon} from "../common/FontIcon";
import ProgressOval from "./ProgressOval";
import {getBootcampUrl} from "../../factories/dashboardPageFactory";
import {navigate} from "../../services/navigation/navigation";

interface IBootcampProgressCard {
  difficultyLevel: string,
  lessonsCompleted?: number,
  lessonsTotal?: number,
}

export default function BootcampProgressCard({difficultyLevel, lessonsCompleted, lessonsTotal}: IBootcampProgressCard) {
  const bootcampUrl: string = useMemo(
    () => getBootcampUrl(difficultyLevel),
    [difficultyLevel] // only re-creates the value for bootcampUrl when difficultyLevel changes
  );
  const onClickNavigateToBootcampHandler = useCallback(
    () => navigate(bootcampUrl),
    [bootcampUrl] // only re-creates the method when bootcampUrl changes
  );

  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, {freezeOnceVisible: true});
  const isVisible = !!entry?.isIntersecting;
  const pathClass = stylePath(isVisible);

  const iconEl = createIconEl(difficultyLevel);
  const computedDashoffset = calculatePercentage(lessonsCompleted ?? 0, lessonsTotal ?? 1);

  return (
    <button className={`bootcamp-progress-card ${difficultyLevel}`} onClick={onClickNavigateToBootcampHandler}>
      <div className={`label-and-icon`}>
        <h2 className={`bootcamp-card-label`}>{difficultyLevel} Bootcamp progress</h2>
        {iconEl}
      </div>
      <div className={`bootcamp-progress-visualization`} ref={ref}>
        <div className={`bootcamp-lesson-completion`}>
          <span className={`lessons-completed`}>{lessonsCompleted ?? `-`}</span> <span className={`total-divider`}>/</span> <span className={`total-lessons`}>{lessonsTotal ?? `-`}</span>
        </div>
        <ProgressOval pathClass={pathClass} computedDashoffset={computedDashoffset} />
      </div>
    </button>
  );
}

function createIconEl(difficultyLevel: string) {
  let iconClassname: Icon = "icon-difficulty-rookie-compact";
  switch (difficultyLevel) {
    case "rookie":
      iconClassname = "icon-difficulty-rookie-compact";
      break;
    case "intermediate":
      iconClassname = "icon-difficulty-intermediate-compact";
      break;
    case "advanced":
      iconClassname = "icon-difficulty-advanced";
      break;
  }

  return <FontIcon icon={iconClassname} />
}

function calculatePercentage(lessonsCompleted: number, lessonsTotal: number) {
  // Ultimately, the path length should probably be determined programmatically using path.getTotalLength();
  const ovalLength = 1093.30;
  const percentage = lessonsCompleted / lessonsTotal;
  return (percentage * ovalLength) + ovalLength;
}

function stylePath(isVisible: boolean) {
  if (isVisible) {
    return 'animate-path';
  } else {
    return 'hide-path';
  }
}
